/* eslint-disable react/prop-types */

import memoizeOne from 'memoize-one';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { accountsTypes, accountsUtils } from 'app-components/flux/accounts';

import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from 'tss-react/mui';

import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DiamondIcon from '@mui/icons-material/Diamond';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocalAtmRoundedIcon from '@mui/icons-material/LocalAtmRounded';
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import PaymentsIcon from '@mui/icons-material/Payments';
import PercentIcon from '@mui/icons-material/Percent';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import StoreRoundedIcon from '@mui/icons-material/StoreRounded';

import { Field } from 'formik';

import NestedQMenu from 'components/NestedQMenu';
import { entitlementsSelectors } from 'app-components/flux/entitlements';

const useStyles = makeStyles()((theme) => ({
  root: {
    cursor: 'pointer',
    paddingRight: '7px',
    alignItems: 'center',
  },
  rootReadOnly: {
    cursor: 'default',
    alignItems: 'center',
    borderRadius: '12px',
    color: 'inherit',
    backgroundColor: theme.palette.background.paper,
    '& .MuiInputBase-root': {
      backgroundColor: theme.palette.background.paper,
      '& .Mui-disabled': {
        WebkitTextFillColor: theme.palette.text.primary,
        color: theme.palette.text.primary,
      },
    },
    '& .Mui-disabled': {
      opacity: 1,
      backgroundColor: theme.palette.background.paper,
      color: 'inherit',
    },
  },
  disabledIcons: {
    opacity: theme.palette.action.disabledOpacity,
  },
}));

const getIconForNode = (node) => {
  const { qcsType } = node;
  switch (qcsType) {
    case 'BANK':
      return AccountBalanceIcon;
    case 'CASH':
      return AccountBalanceIcon;
    case 'CREDIT':
      return CreditCardIcon;
    case 'LOAN':
      return PercentIcon;
    case 'REAL_ESTATE':
      return MapsHomeWorkIcon;
    case 'VEHICLE':
      return DirectionsCarIcon;
    case 'OTHER_ASSET':
      return DiamondIcon;
    case 'INVESTMENT':
      return StackedLineChartIcon;
    case 'PAYMENT':
      return PaymentsIcon;
    case 'BUSINESS':
      return StoreRoundedIcon;
    default:
      return LocalAtmRoundedIcon;
  }
};

const getMenuOptionForNode = (
  node,
  depth,
  showManualTypes,
  initialNode,
  showManualInvestment,
  businessEnabled,
  mode,
) => {
  if (!accountsUtils.isAccountNodeSelectable(node, initialNode, businessEnabled, mode)) {
    return null;
  }
  if (
    node.selectable &&
    (node.connectable || showManualTypes) &&
    (!showManualTypes || node.id !== 'INVESTMENT' || (showManualTypes && showManualInvestment)) // temporary until manual holdings released
  ) {
    if (node.children) {
      const childMenuItems = node.children.reduce((menuItems, child) => {
        const menuItem = getMenuOptionForNode(
          child,
          depth + 1,
          showManualTypes,
          initialNode,
          showManualInvestment,
          businessEnabled,
          mode,
        );
        if (menuItem != null) {
          if (Array.isArray(menuItem)) {
            menuItems.push(...menuItem);
          } else {
            menuItems.push(menuItem);
          }
        }
        return menuItems;
      }, []);

      if (node.business && depth > 0 && childMenuItems.length > 0) {
        return [
          { nop: true, divider: true },
          { nop: true, isSubheader: true, label: node.displayName },
          ...childMenuItems,
        ];
      }

      const Icon = getIconForNode(node);
      return {
        ...(depth === 0 && { startIcon: <Icon /> }),
        label: node.displayName,
        value: node,
        onSelect: () => true,
        subMenu: childMenuItems,
      };
    }
    return { label: node.displayName, value: node };
  }
  return null;
};

function getMenuOptionsForNode(initialNode, showManualTypes, showManualInvestment, businessEnabled, mode) {
  return accountsTypes.AccountTypeNodesTree.reduce((menuItems, node) => {
    const menuItem = getMenuOptionForNode(
      node,
      0,
      showManualTypes,
      initialNode,
      showManualInvestment,
      businessEnabled,
      mode,
    );
    if (menuItem != null) {
      if (Array.isArray(menuItem)) {
        menuItems.push(...menuItem);
      } else {
        menuItems.push(menuItem);
      }
    }
    return menuItems;
  }, []);
}
const memoizeGetMenuOptions = memoizeOne(getMenuOptionsForNode);

const AccountTypeSelector = (props) => {
  const {
    currentAccountType,
    name = 'accountType',
    size = 'medium',
    showManualTypes,
    sx,
    TextFieldProps = {},
    onChange,
    ...other
  } = props;
  const { InputProps = {}, ...otherTextFieldProps } = TextFieldProps;

  const inputRef = useRef();
  const { classes } = useStyles();

  const businessEnabled = useSelector(entitlementsSelectors.getBusinessEnabled);
  const mode = currentAccountType ? 'edit' : 'add';

  const [anchorEl, setAnchorEl] = useState(null);

  const handleChange = (form) => (val, _event) => {
    form.setFieldValue(name, val);
    onChange?.(val);
    setAnchorEl(null);
  };

  const showTip = inputRef.current && inputRef.current.offsetWidth < inputRef.current.scrollWidth;

  return (
    <Field name={name} {...other}>
      {({ field, form }) => {
        const Icon = field.value ? getIconForNode(field.value) : null;

        const accountTypeFormValue =
          field.value?.displayName || currentAccountType?.displayName || form?.initialValues[name]?.displayName || '';
        return (
          <Tooltip title={showTip ? accountTypeFormValue : ''}>
            <Box
              // So not remove this div - it is important for catching the tooltip ref, as field
              // is a 3rd party functional component and therefore cannot hold a ref
              sx={sx}
            >
              <TextField
                {...field}
                className={otherTextFieldProps.readOnly ? classes.rootReadOnly : undefined}
                label={'Account Type'}
                onChange={() => true}
                onClick={(e) =>
                  !otherTextFieldProps.disabled && !otherTextFieldProps.readOnly && setAnchorEl(e.currentTarget)
                }
                disabled={otherTextFieldProps.disabled || otherTextFieldProps.readOnly}
                onKeyDown={(e) => {
                  if (e.key === ' ' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                    e.preventDefault();
                    setAnchorEl(e.currentTarget);
                  }
                }}
                size={size}
                value={accountTypeFormValue}
                InputProps={{
                  classes: {
                    root: classes.root,
                    input: classes.root,
                  },
                  endAdornment: !otherTextFieldProps.readOnly ? (
                    <InputAdornment position='end' sx={{ ...(anchorEl && { transform: 'rotate(180deg)' }) }}>
                      <ArrowDropDownRoundedIcon className={otherTextFieldProps.disabled ? classes.disabledIcons : ''} />
                    </InputAdornment>
                  ) : null,
                  inputProps: {
                    ref: inputRef,
                    sx: { ...(size === 'small' && { typography: 'body2' }) },
                  },
                  readOnly: true,
                  ...(Icon && {
                    startAdornment: (
                      <InputAdornment position='start' size='small'>
                        <Icon fontSize={size} className={otherTextFieldProps.disabled ? classes.disabledIcons : ''} />
                      </InputAdornment>
                    ),
                  }),
                  variant: 'ellipsis',
                  ...InputProps,
                }}
                sx={sx}
                {...otherTextFieldProps}
              />
              <NestedQMenu
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                options={memoizeGetMenuOptions(currentAccountType, showManualTypes, true, businessEnabled, mode)}
                onChange={handleChange(form)}
                onClose={() => setAnchorEl(null)}
                component='span'
                isNestedMenu
              />
            </Box>
          </Tooltip>
        );
      }}
    </Field>
  );
};
export default AccountTypeSelector;
